import Constants from '../Constants.js';
import OidcClientReducer from './OidcClientReducer.js';
const {
  getStatus,
  getError,
  getOidcClients
} = OidcClientReducer;

/**
 * @class OidcClients An interface for all OIDC Clients within a project
 * @property {string} projectId The ID of the project that contains your OIDC Clients. 
 * If not provided, `process.env.projectId` will be used. 
 */
class OidcClients {
  constructor(projectId) {
    this.projectId = projectId || process?.env?.projectId;
  }

  /**
   * @method createAction A creator for actions
   * @memberof OidcClients
   * @param {string} method The name of the method being created (e.g. 'list')
   * @param {string} status The status of the action (e.g. 'loading')
   * @param {function} func The async function that executes the action
   */
  createAction(method, status, func) {
    return (dispatch, getState, opts) => {
      const projectId = this.projectId || opts?.api?.projectId;
      dispatch({
        type: Constants.OIDC_CLIENT_STATUS,
        status,
        projectId
      });
      return func(dispatch, getState, opts, projectId).catch(error => {
        dispatch({
          type: Constants.OIDC_CLIENT_STATUS,
          projectId,
          status: `${method}Error`,
          error
        });
        return Promise.reject(error);
      });
    };
  }

  /**
   * @method list An action to list all OIDC Clients within the project
   * @memberof OidcClients
   * @param {object} opts Options for the `oidcClients.list( ...opts )` api call
   * @returns {promise}
   */
  list(opts = {}) {
    return this.createAction('list', 'loading', async (dispatch, getState, {
      api
    }, projectId) => {
      const listOidcClientsRes = await api.project(projectId).oidcClients().list(opts);
      dispatch({
        type: Constants.OIDC_CLIENT_LIST,
        projectId,
        oidcClients: listOidcClientsRes.items
      });
      return listOidcClientsRes;
    });
  }

  /**
   * @method create An action to create an OIDC Client
   * @memberof OidcClients
   * @param {object} oidcClient The data with which to create the OIDC Client
   * @returns {promise}
   */
  create(oidcClient) {
    return this.createAction('create', 'creating', async (dispatch, getState, {
      api
    }, projectId) => {
      const createOidcClientRes = await api.project(projectId).oidcClients().create(oidcClient);
      dispatch({
        type: Constants.OIDC_CLIENT_ADD,
        projectId,
        oidcClient: createOidcClientRes
      });
      return createOidcClientRes;
    });
  }

  /**
   * @method getStatus Get the status of the OidcClient actions from Redux state
   * @memberof OidcClient
   * @returns {promise}
   */
  getStatus(state) {
    return getStatus(state, this.projectId);
  }

  /**
   * @method getError Get the current error for the OidcClient actions from Redux state
   * @memberof OidcClient
   * @returns {promise}
   */
  getError(state) {
    return getError(state, this.projectId);
  }

  /**
   * @method getOidcClients Get the project's list of OidcClients from Redux state
   * @memberof OidcClient
   * @returns {promise}
   */
  getOidcClients(state) {
    return getOidcClients(state, this.projectId);
  }
}
export default OidcClients;